import { Badge } from '@/components/ui/badge';
import { TableSkeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthorizationStore } from '../store';
import type { PermissionHistoryDto } from '../types';

interface HistoryGroup {
  batchId: string;
  changedBy: string;
  changedAt: string;
  entries: PermissionHistoryDto[];
}

function groupByBatch(history: PermissionHistoryDto[]): HistoryGroup[] {
  const groups = new Map<string, HistoryGroup>();

  // Sort by changedAt descending (newest first)
  const sorted = [...history].sort(
    (a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
  );

  for (const entry of sorted) {
    const key = entry.changeBatchId || `single-${entry.permissionCode}-${entry.changedAt}`;
    const existing = groups.get(key);
    if (existing) {
      existing.entries.push(entry);
    } else {
      groups.set(key, {
        batchId: key,
        changedBy: entry.changedBy,
        changedAt: entry.changedAt,
        entries: [entry],
      });
    }
  }

  return Array.from(groups.values());
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PermissionHistoryModal() {
  const {
    permissionHistoryModalOpen,
    closePermissionHistoryModal,
    permissionHistory,
    isLoadingPermissionHistory,
    selectedRoleForAuth,
  } = useAuthorizationStore();

  const groups = groupByBatch(permissionHistory);

  return (
    <Dialog
      open={permissionHistoryModalOpen}
      onOpenChange={(open) => !open && closePermissionHistoryModal()}
    >
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Permission History</DialogTitle>
          <DialogDescription>
            Change log for role{' '}
            <span className="font-medium text-foreground">
              {selectedRoleForAuth?.name}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {isLoadingPermissionHistory ? (
            <TableSkeleton rows={6} cols={2} className="py-2" />
          ) : groups.length === 0 ? (
            <div className="text-center text-muted-foreground py-12 text-sm">
              No permission history found for this role.
            </div>
          ) : (
            <div className="space-y-6">
              {groups.map((group) => (
                <div key={group.batchId} className="space-y-2">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                      Changed by{' '}
                      <span className="font-medium text-foreground">
                        {group.changedBy}
                      </span>
                    </span>
                    <span>{formatDate(group.changedAt)}</span>
                  </div>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Permission</TableHead>
                          <TableHead className="w-28">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.entries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-sm">
                              {entry.permissionCode}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={entry.isGranted ? 'success' : 'destructive'}
                                className="text-xs"
                              >
                                {entry.isGranted ? 'Granted' : 'Revoked'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
